import React, { useCallback, useMemo } from "react";
import { ArrowRight, BarChart3 } from "lucide-react";
import { clinicianPortalApi } from "../api/clinicianPortalApi";
import { getClinicianPortalClientSnapshot } from "../api/portalRequestCache";
import {
  CompletedHomeworkByActivityTypeChart,
  buildCompletedHomeworkActivityTypeSlices,
} from "../components/CompletedHomeworkByActivityTypeChart";
import { ErrorState, LoadingState } from "../components/FeedbackStates";
import { HomeworkEngagementSummary } from "../components/HomeworkEngagementSummary";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import {
  ClientPrimaryTabs,
  SegmentedNavigation,
} from "../components/PortalNavigation";
import { insightsNavigation } from "../config/navigation";
import { usePortalData } from "../hooks/usePortalData";
import {
  clientWorkspaceBreadcrumbs,
  useClientWorkspace,
} from "../components/ClientWorkspace";

export function buildClientUsageAnalyticsUrl(
  accessCode: string,
  nickname: string,
  range = "last_30_days",
  tab?: "safety",
): string {
  const search = new URLSearchParams({
    accessCode,
    nickname,
    range,
  });
  if (tab) search.set("tab", tab);
  return `/reports/client?${search.toString()}`;
}

export function UsageEngagementPage({
  clinicianCode,
  patientId,
}: {
  clinicianCode: string;
  patientId: string;
}) {
  const workspace = useClientWorkspace();
  const request = useCallback(
    (signal: AbortSignal) =>
      workspace.accessScope === "team"
        ? clinicianPortalApi.getHomeworkHistory(
            clinicianCode,
            patientId,
            "all-activities",
            signal,
            "team",
          )
        : clinicianPortalApi.getHomeworkHistory(
            clinicianCode,
            patientId,
            "all-activities",
            signal,
          ),
    [clinicianCode, patientId, workspace.accessScope],
  );
  const engagementRequest = useCallback(
    (signal: AbortSignal) =>
      workspace.accessScope === "team"
        ? clinicianPortalApi.getClient(
            clinicianCode,
            patientId,
            true,
            signal,
            "team",
          )
        : clinicianPortalApi.getClient(clinicianCode, patientId, true, signal),
    [clinicianCode, patientId, workspace.accessScope],
  );
  const state = usePortalData(request);
  const engagementState = usePortalData(engagementRequest);
  const snapshot = getClinicianPortalClientSnapshot(patientId, workspace.accessScope);
  const data =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const available = Boolean(
    data?.found &&
      data.authorized &&
      data.therapist &&
      data.patient &&
      data.historyFilter === "all-activities",
  );
  const patient = available ? data?.patient : snapshot?.patient;
  const therapist = available ? data?.therapist : snapshot?.therapist;
  const engagementData =
    engagementState.status === "success" ||
    engagementState.status === "refreshing"
      ? engagementState.data
      : undefined;
  const engagementAvailable = Boolean(
    engagementData?.found &&
      engagementData.authorized &&
      engagementData.therapist &&
      engagementData.patient?.id === patientId &&
      engagementData.homeworkEngagement,
  );
  const activityTypeSlices = useMemo(
    () =>
      buildCompletedHomeworkActivityTypeSlices(
        available ? (data?.assignments ?? []) : [],
      ),
    [available, data?.assignments],
  );

  return (
    <ClinicianPortalLayout
      activeGlobalKey={workspace.source === "team" ? "team" : "clients"}
      clinicianCode={clinicianCode}
      therapistRole={therapist?.role}
    >
      <PortalHeader
        density="compact"
        title={
          patient
            ? `Client Record: ${patient.accessCode} • ${patient.nickname}`
            : "Client Record"
        }
        clinicianName={therapist?.therapistName}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={clientWorkspaceBreadcrumbs(
              workspace,
              patientId,
              patient?.nickname || "Client",
            )}
          />
        }
      >
        <ClientPrimaryTabs
          patientId={patientId}
          clinicianCode={clinicianCode}
          activeKey="insights"
        />
      </PortalHeader>
      <div className="cp-page-content cp-page-content--profile cp-page-content--compact-client cp-page-content--sticky-secondary-nav">
        <SegmentedNavigation
          items={insightsNavigation}
          patientId={patientId}
          clinicianCode={clinicianCode}
          activeKey="usage-engagement"
        />
        {state.status === "loading" ? (
          <LoadingState label="Loading usage and engagement" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState
            error={new Error("Usage and engagement data is not available")}
          />
        ) : null}
        {available && patient ? (
          <section aria-labelledby="usage-engagement-heading">
            <div className="cp-content-header cp-content-header--route-intro">
              <div>
                <h2
                  id="usage-engagement-heading"
                  className="cp-visually-hidden"
                >
                  Usage &amp; Engagement
                </h2>
                <p>
                  Review how this client uses and engages with the assistant.
                </p>
              </div>
            </div>
            <div className="cp-usage-engagement__content">
              {engagementState.status === "loading" ||
              (engagementState.status === "refreshing" &&
                !engagementAvailable) ? (
                <LoadingState label="Loading homework engagement" />
              ) : null}
              {engagementState.status === "error" ? (
                <ErrorState
                  error={engagementState.error}
                  onRetry={engagementState.refresh}
                />
              ) : null}
              {engagementState.status === "success" &&
              engagementData &&
              !engagementAvailable ? (
                <ErrorState
                  error={new Error("Homework engagement is not available")}
                  onRetry={engagementState.refresh}
                />
              ) : null}
              {engagementAvailable && engagementData?.homeworkEngagement ? (
                <HomeworkEngagementSummary
                  {...engagementData.homeworkEngagement}
                />
              ) : null}
              <section
                className="cp-chart-card"
                aria-labelledby="completed-homework-activity-type-heading"
              >
                <div className="cp-chart-card__header">
                  <div>
                    <h3 id="completed-homework-activity-type-heading">
                      Completed Homework by Activity Type
                    </h3>
                    <p>
                      Includes completed activities and activities completed
                      with modification.
                    </p>
                  </div>
                </div>
                <CompletedHomeworkByActivityTypeChart
                  slices={activityTypeSlices}
                />
              </section>

              <a
                className="cp-usage-analytics-card"
                href={buildClientUsageAnalyticsUrl(
                  patient.accessCode,
                  patient.nickname,
                )}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span
                  className="cp-usage-analytics-card__icon"
                  aria-hidden="true"
                >
                  <BarChart3 />
                </span>
                <span className="cp-usage-analytics-card__content">
                  <strong>Detailed Usage Analytics</strong>
                  <span>
                    Review communication modes, devices, interactive tool usage,
                    and safety and reassurance detections.
                  </span>
                </span>
                <span className="cp-usage-analytics-card__action">
                  View Full Usage Analytics <ArrowRight aria-hidden="true" />
                </span>
              </a>
            </div>
          </section>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
